import React, { useState, useEffect } from "react";
import axios from "axios";
import { format, parseISO } from "date-fns";
import { pt } from "date-fns/locale";
import { toast } from "sonner";
import { FileText, Search, Filter, User, UserCog, Check, Star, CreditCard, GraduationCap, History } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { API } from "../../App";

const AdminLogs = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterAction, setFilterAction] = useState("all");

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const response = await axios.get(`${API}/admin/activity-logs?limit=100`, { withCredentials: true });
      setLogs(response.data);
    } catch (error) {
      toast.error("Erro ao carregar histórico");
    } finally {
      setLoading(false);
    }
  };

  const getActionIcon = (action) => {
    switch (action) {
      case "attendance_marked": return <Check className="h-4 w-4" />;
      case "credits_updated":
      case "credits_deducted": return <CreditCard className="h-4 w-4" />;
      case "role_changed": return <UserCog className="h-4 w-4" />;
      case "review_created": return <Star className="h-4 w-4" />;
      case "subscription_cancelled":
      case "subscription_created": return <GraduationCap className="h-4 w-4" />;
      default: return <History className="h-4 w-4" />;
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case "attendance_marked": return "bg-green-100 text-green-600";
      case "credits_updated": return "bg-blue-100 text-blue-600";
      case "credits_deducted": return "bg-orange-100 text-orange-600";
      case "role_changed": return "bg-purple-100 text-purple-600";
      case "review_created": return "bg-yellow-100 text-yellow-600";
      case "subscription_created": return "bg-teal-100 text-teal-600";
      case "subscription_cancelled": return "bg-red-100 text-red-600";
      default: return "bg-gray-100 text-gray-600";
    }
  };

  const getActionLabel = (action) => {
    const labels = {
      "attendance_marked": "Presença",
      "credits_updated": "Créditos",
      "credits_deducted": "Dedução",
      "role_changed": "Cargo",
      "review_created": "Review",
      "subscription_created": "Subscrição",
      "subscription_cancelled": "Cancelamento",
      "subscription_updated": "Sub. Atualizada",
      "payment_marked": "Pagamento",
      "enrollment_created": "Inscrição",
      "enrollment_cancelled": "Canc. Inscrição",
      "lesson_created": "Aula Criada",
      "lesson_updated": "Aula Atualizada",
      "lesson_deleted": "Aula Eliminada",
      "user_blocked": "Bloqueado",
      "user_unblocked": "Desbloqueado",
      "settings_updated": "Configurações",
    };
    return labels[action] || action.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
  };

  const actionTypes = [...new Set(logs.map(l => l.action))];
  const filteredLogs = filterAction === "all" ? logs : logs.filter(l => l.action === filterAction);

  // Group by date
  const grouped = filteredLogs.reduce((acc, log) => {
    const dateKey = log.created_at?.slice(0, 10) || "unknown";
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(log);
    return acc;
  }, {});

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[#3d5a63] mb-2">Histórico de Atividades</h1>
          <p className="text-[#475569]">Registo de todas as ações realizadas no sistema</p>
        </div>
        <Badge variant="outline" className="text-[#6697a4]">{filteredLogs.length} registos</Badge>
      </div>

      {/* Filters */}
      <div className="flex gap-2 flex-wrap">
        <Button variant={filterAction === "all" ? "default" : "outline"} size="sm" className={filterAction === "all" ? "bg-[#6697a4] rounded-full" : "rounded-full"} onClick={() => setFilterAction("all")}>Todos</Button>
        {actionTypes.map(a => (
          <Button key={a} variant={filterAction === a ? "default" : "outline"} size="sm" className={filterAction === a ? "bg-[#6697a4] rounded-full" : "rounded-full"} onClick={() => setFilterAction(a)}>{getActionLabel(a)}</Button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="w-12 h-12 border-4 border-[#6697a4] border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : filteredLogs.length === 0 ? (
        <Card className="bg-white border-0 shadow-sm rounded-xl">
          <CardContent className="p-12 text-center">
            <History className="h-12 w-12 text-[#94A3B8] mx-auto mb-4" />
            <p className="text-[#475569]">Sem atividades registadas</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {Object.entries(grouped).map(([dateKey, dateLogs]) => (
            <div key={dateKey}>
              <div className="flex items-center gap-3 mb-4">
                <div className="h-px flex-1 bg-slate-200" />
                <span className="text-xs font-semibold text-[#94A3B8] uppercase tracking-wider">
                  {(() => { try { return format(parseISO(dateKey), "d 'de' MMMM yyyy", { locale: pt }); } catch { return dateKey; } })()}
                </span>
                <div className="h-px flex-1 bg-slate-200" />
              </div>
              <div className="space-y-6">
                {dateLogs.map((log) => (
                  <Card key={log.log_id} className="border-0 shadow-sm rounded-xl hover:shadow-md transition-shadow">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${getActionColor(log.action)}`}>
                          {getActionIcon(log.action)}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-[#3d5a63] text-sm">{log.details}</p>
                          <div className="flex items-center gap-3 mt-1.5 text-xs text-[#94A3B8]">
                            <span className="flex items-center gap-1"><User className="h-3 w-3" /> {log.actor_name}</span>
                            <Badge variant="outline" className="text-[10px] px-1.5 py-0">{log.actor_role}</Badge>
                            {log.target_name && <span>Alvo: {log.target_name}</span>}
                          </div>
                        </div>
                        <span className="text-xs text-[#94A3B8] shrink-0">
                          {(() => { try { return format(parseISO(log.created_at), "HH:mm", { locale: pt }); } catch { return ""; } })()}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Admin Packs Management

export default AdminLogs;
